import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { getImageUrl, getThumbnailUrl } from '../../utils/api';
import useScrollLock from '../../hooks/useScrollLock';
import { useLanguage } from '../../contexts/LanguageContext';
import './Salon.css';

const ImageViewerModal = ({ isOpen, onClose, images, currentIndex = 0 }) => {
  const { t } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(currentIndex);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [thumbnailStartIndex, setThumbnailStartIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  // Constants for thumbnail navigation
  const MAX_VISIBLE_THUMBNAILS = 5;
  const MIN_VISIBLE_THUMBNAILS = 1;
  
  // Calculate responsive thumbnail count based on window width
  const getVisibleThumbnailCount = useCallback(() => {
    if (windowWidth >= 800) return MAX_VISIBLE_THUMBNAILS; // 5 thumbnails
    if (windowWidth >= 500) return 4;
    if (windowWidth >= 430) return 4;
    if (windowWidth >= 340) return 3;
    if (windowWidth >= 300) return 2;
    return MIN_VISIBLE_THUMBNAILS;
  }, [windowWidth]);
  
  const visibleThumbnailCount = getVisibleThumbnailCount();

  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  // Calculate if current item is video (moved up to avoid initialization issues)
  const currentImage = useMemo(() => {
    return images && images.length > 0 ? images[currentImageIndex] : null;
  }, [images, currentImageIndex]);
  
  const isVideo = useMemo(() => {
    return currentImage && typeof currentImage === 'object' && currentImage.type === 'video';
  }, [currentImage]);

  // Handle container resize
  const handleResize = useCallback(() => {
    if (imageSize.width && imageSize.height) {
      // Use viewport dimensions instead of container dimensions
      const headerHeight = 60;
      const footerHeight = 60;
      const thumbnailsHeight = 0; // Thumbnails are now in the footer
      const padding = 40;
      
      const availableHeight = window.innerHeight - headerHeight - footerHeight - thumbnailsHeight - padding;
      const availableWidth = window.innerWidth - 100;
      
      setContainerSize({
        width: Math.max(availableWidth, 400),
        height: Math.max(availableHeight, 300)
      });
    }
  }, [imageSize.width, imageSize.height]);

  const goToPrevious = useCallback(() => {
    setCurrentImageIndex(prev => (prev > 0 ? prev - 1 : prev));
  }, []);

  const goToNext = useCallback(() => {
    setCurrentImageIndex(prev => (prev < images.length - 1 ? prev + 1 : prev));
  }, [images.length]);

  // Helper functions for thumbnail navigation
  const getVisibleThumbnails = useCallback(() => {
    if (images.length <= visibleThumbnailCount) {
      return images.map((image, index) => ({ image, index }));
    }
    
    const endIndex = Math.min(thumbnailStartIndex + visibleThumbnailCount, images.length);
    const visibleImages = [];
    
    for (let i = thumbnailStartIndex; i < endIndex; i++) {
      visibleImages.push({ image: images[i], index: i });
    }
    
    return visibleImages;
  }, [images, thumbnailStartIndex, visibleThumbnailCount]);

  useEffect(() => {
    setCurrentImageIndex(currentIndex);
    // Reset zoom and pan when changing images
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [currentIndex, isOpen]);

  // Auto-adjust thumbnail start index to keep current image visible
  useEffect(() => {
    if (images.length <= visibleThumbnailCount) {
      setThumbnailStartIndex(0);
      return;
    }

    // If current image is not visible in the thumbnail range, adjust the start index
    const endIndex = thumbnailStartIndex + visibleThumbnailCount - 1;
    if (currentImageIndex < thumbnailStartIndex) {
      // Current image is before the visible range, scroll left
      setThumbnailStartIndex(currentImageIndex);
    } else if (currentImageIndex > endIndex) {
      // Current image is after the visible range, scroll right
      setThumbnailStartIndex(currentImageIndex - visibleThumbnailCount + 1);
    }
  }, [currentImageIndex, images.length, visibleThumbnailCount, thumbnailStartIndex]);

  // Handle window resize
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isOpen, handleResize]);

  // Handle window width changes for responsive thumbnails
  useEffect(() => {
    const handleWindowResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  // Update container size when modal opens
  useEffect(() => {
    if (isOpen && containerRef.current) {
      handleResize();
    }
  }, [isOpen, handleResize]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          goToNext();
          break;
        case '+':
        case '=':
          if (!isVideo) {
            e.preventDefault();
            handleZoomIn();
          }
          break;
        case '-':
          if (!isVideo) {
            e.preventDefault();
            handleZoomOut();
          }
          break;
        case '0':
          if (!isVideo) {
            e.preventDefault();
            handleResetZoom();
          }
          break;
        default:
          // No action for other keys
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentImageIndex, goToNext, goToPrevious, onClose, isVideo]);


  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 5)); // Max zoom 5x
  };

  const handleZoomOut = () => {
    setZoom(prev => {
      const newZoom = prev / 1.2;
      if (newZoom < 0.5) {
        setPan({ x: 0, y: 0 }); // Reset pan when zooming out to minimum
        return 0.5;
      }
      return newZoom;
    });
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleDownload = () => {
    if (!currentImage) return;
    
    try {
      // Create a temporary anchor element for download
      const link = document.createElement('a');
      link.href = isVideo ? currentImage.url : getImageUrl(currentImage);
      
      // Extract filename from URL or create a default name
      const urlParts = link.href.split('/');
      const filename = urlParts[urlParts.length - 1] || `download_${Date.now()}.${isVideo ? 'mp4' : 'jpg'}`;
      
      link.download = filename;
      link.target = '_blank';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      // Fallback: open in new tab
      window.open(isVideo ? currentImage.url : getImageUrl(currentImage), '_blank');
    }
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Calculate the proper dimensions to fit image in container
  const calculateFitDimensions = (imgWidth, imgHeight, containerWidth, containerHeight) => {
    if (!imgWidth || !imgHeight || !containerWidth || !containerHeight) {
      return { width: 0, height: 0 };
    }

    const imgAspectRatio = imgWidth / imgHeight;
    const containerAspectRatio = containerWidth / containerHeight;

    let fitWidth, fitHeight;

    if (imgAspectRatio > containerAspectRatio) {
      // Image is wider - fit to width
      fitWidth = containerWidth;
      fitHeight = containerWidth / imgAspectRatio;
    } else {
      // Image is taller - fit to height
      fitHeight = containerHeight;
      fitWidth = containerHeight * imgAspectRatio;
    }

    return { width: fitWidth, height: fitHeight };
  };

  // Handle image/video load to get natural dimensions
  const handleImageLoad = () => {
    if (imageRef.current) {
      const element = imageRef.current;
      
      if (isVideo) {
        // For videos, use videoWidth and videoHeight
        setImageSize({
          width: element.videoWidth || 1920, // Default fallback
          height: element.videoHeight || 1080 // Default fallback
        });
      } else {
        // For images, use naturalWidth and naturalHeight
        setImageSize({
          width: element.naturalWidth,
          height: element.naturalHeight
        });
      }
      
      // Use viewport dimensions instead of container dimensions
      // Account for header and footer space
      const headerHeight = 60; // Approximate header height
      const footerHeight = 60; // Approximate footer height
      const thumbnailsHeight = 0; // Thumbnails are now in the footer
      const padding = 40; // Extra padding
      
      const availableHeight = window.innerHeight - headerHeight - footerHeight - thumbnailsHeight - padding;
      const availableWidth = window.innerWidth - 100; // Account for modal padding
      
      setContainerSize({
        width: Math.max(availableWidth, 400), // Minimum width
        height: Math.max(availableHeight, 300) // Minimum height
      });
    }
  };


  if (!isOpen || !images || images.length === 0) {
    return null;
  }

  const fitDimensions = calculateFitDimensions(
    imageSize.width, 
    imageSize.height, 
    containerSize.width, 
    containerSize.height
  );

  const isAtFirst = currentImageIndex === 0;
  const isAtLast = currentImageIndex === images.length - 1;

  return (
    <div className="image-viewer-overlay" onClick={onClose}>
      <div className="image-viewer-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="image-viewer-header">
          <div className="image-counter">
            {currentImageIndex + 1} {t('common.imageViewer.of')} {images.length}
          </div>
          <div className="image-viewer-zoom-info">
            {isVideo ? t('common.imageViewer.video') : `${Math.round(zoom * 100)}%`}
          </div>
          <div className="image-viewer-actions">
            <button 
              className="image-viewer-download" 
              onClick={handleDownload}
              title={isVideo ? t('common.tooltips.downloadVideo') : t('common.tooltips.downloadImage')}
            >
              <FontAwesomeIcon icon={faDownload} />
            </button>
            <button className="image-viewer-close" onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        {/* Main Image Container */}
        <div 
          className="image-viewer-content"
          onWheel={!isVideo ? handleWheel : undefined}
          onMouseMove={!isVideo ? handleMouseMove : undefined}
          onMouseUp={!isVideo ? handleMouseUp : undefined}
          onMouseLeave={!isVideo ? handleMouseUp : undefined}
        >
          <button 
            className="image-viewer-nav image-viewer-prev" 
            onClick={goToPrevious}
            disabled={isAtFirst}
          >
            ‹
          </button>
          
          <div className="image-viewer-main" ref={containerRef}>
            {isVideo ? (
              <video 
                ref={imageRef}
                src={currentImage.url}
                controls
                className="image-viewer-video"
                style={{
                  width: fitDimensions.width > 0 ? `${fitDimensions.width}px` : 'auto',
                  height: fitDimensions.height > 0 ? `${fitDimensions.height}px` : 'auto',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
                onLoadedMetadata={handleImageLoad}
                preload="metadata"
              />
            ) : (
              <img 
                ref={imageRef}
                src={getImageUrl(currentImage)} 
                alt={`${currentImageIndex + 1} of ${images.length}`}
                className="image-viewer-img zoomable"
                style={{
                  width: fitDimensions.width > 0 ? `${fitDimensions.width}px` : 'auto',
                  height: fitDimensions.height > 0 ? `${fitDimensions.height}px` : 'auto',
                  transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                  cursor: isDragging ? 'grabbing' : 'grab'
                }}
                onMouseDown={handleMouseDown}
                onLoad={handleImageLoad}
                draggable={false}
              />
            )}
            
            {/* Zoom Controls Overlay - Only show for images */}
            {!isVideo && (
              <div className="image-viewer-zoom-overlay">
                <button 
                  className="image-viewer-zoom-overlay-btn" 
                  onClick={handleZoomOut}
                  disabled={zoom <= 0.5}
                  title={t('common.tooltips.zoomOut')}
                >
                  −
                </button>
                <button 
                  className="image-viewer-zoom-overlay-btn" 
                  onClick={handleResetZoom}
                  title={t('common.tooltips.resetZoom')}
                >
                  {t('common.imageViewer.reset')}
                </button>
                <button 
                  className="image-viewer-zoom-overlay-btn" 
                  onClick={handleZoomIn}
                  disabled={zoom >= 5}
                  title={t('common.tooltips.zoomIn')}
                >
                  +
                </button>
              </div>
            )}
          </div>
          
          <button 
            className="image-viewer-nav image-viewer-next" 
            onClick={goToNext}
            disabled={isAtLast}
          >
            ›
          </button>
        </div>

        {/* Footer with controls and thumbnails */}
        <div className="image-viewer-footer">
          {/* Thumbnail Navigation */}
          {images.length > 1 && (
            <div className="image-viewer-thumbnails">
              <button
                className="thumbnail-nav-btn thumbnail-nav-prev"
                onClick={goToPrevious}
                disabled={isAtFirst}
                title={t('common.tooltips.previousImage')}
              >
                ‹
              </button>
              
              {/* Thumbnail list */}
              <div className="thumbnail-list">
                {getVisibleThumbnails().map(({ image, index }) => {
                  const isVideoThumbnail = image && typeof image === 'object' && image.type === 'video';
                  return (
                    <button
                      key={index}
                      className={`image-viewer-thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                      onClick={() => setCurrentImageIndex(index)}
                    >
                      {isVideoThumbnail ? (
                        <div className="video-thumbnail">
                          <video 
                            src={image.thumbnail} 
                            muted 
                            preload="metadata"
                            style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                          />
                          <div className="video-play-icon">▶</div>
                        </div>
                      ) : (
                        <img src={getThumbnailUrl(image)} alt={`Thumbnail ${index + 1}`} />
                      )}
                    </button>
                  );
                })}
              </div>
              
              <button
                className="thumbnail-nav-btn thumbnail-nav-next"
                onClick={goToNext}
                disabled={isAtLast}
                title={t('common.tooltips.nextImage')}
              >
                ›
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageViewerModal;
